import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "389",
  "slug": "what-is-a-fjord-how-they-form",
  "title": "What Is a Fjord? How These Dramatic Coastlines Form",
  "excerpt": "Steep walls, deep water, and a winding path inland: fjords are among the most striking coastlines on Earth. Here is how glaciers carved them and where to find the best ones.",
  "date": "2026-06-09",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1663428520845-056989f8a664?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxub3J3YXklMjBmam9yZHxlbnwxfDB8fHwxNzgwOTkyODMyfDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "A fjord is a long, narrow, deep inlet of the sea formed when a glacier carved out a steep valley that was later flooded by rising sea levels. Fjords are found mainly in formerly glaciated regions like Norway, Chile, New Zealand, Greenland, Alaska, and Canada.",
  "content": [
    {
      "type": "paragraph",
      "text": "Few landscapes are as instantly recognizable as a fjord: a ribbon of deep blue water threading between sheer rock walls, often with waterfalls spilling down from above. They look almost designed for postcards. But fjords are not just scenic; they are a record of the ice ages written into the coastline. Understanding how they form turns a pretty view into a geography lesson you can read at a glance."
    },
    {
      "type": "heading",
      "text": "What Exactly Is a Fjord?"
    },
    {
      "type": "paragraph",
      "text": "A fjord is a long, narrow, deep inlet of the sea that reaches inland between steep slopes or cliffs. The defining feature is the combination of depth and steepness: fjords are often far deeper than the open sea just outside them, and their walls can rise hundreds or even thousands of metres almost straight up from the water. They wind and branch inland, sometimes for tens of kilometres."
    },
    {
      "type": "heading",
      "text": "How Glaciers Carve Them"
    },
    {
      "type": "paragraph",
      "text": "Fjords are the work of ice. During the ice ages, thick glaciers flowed slowly down existing river valleys toward the sea. Unlike water, which cuts a narrow V-shaped channel, a glacier is a heavy, grinding mass that scours out a wide, deep, U-shaped trough. The ice plucked away rock and dragged debris along, deepening the valley far below the level a river ever could."
    },
    {
      "type": "paragraph",
      "text": "When the ice ages ended and the glaciers melted, two things happened. The valleys were left as deep, steep-walled troughs, and global sea levels rose as all that ice returned to the oceans. The sea flooded into the carved valleys, and a fjord was born: a drowned glacial valley open to the sea."
    },
    {
      "type": "heading",
      "text": "The Telltale Sign: The Sill"
    },
    {
      "type": "paragraph",
      "text": "Many fjords have a hidden feature at their mouth called a sill, a shallower ridge of rock and glacial debris where the glacier finally lost its power and dropped its load. The water behind the sill can be enormously deep, while the entrance is comparatively shallow. This sill can partly trap deep water inside the fjord, creating unusual conditions and rich ecosystems in the sheltered basin behind it."
    },
    {
      "type": "heading",
      "text": "Where to Find the Best Fjords"
    },
    {
      "type": "paragraph",
      "text": "Fjords appear wherever glaciers once flowed down to a coast, which means they cluster at high latitudes and in cold mountainous regions:"
    },
    {
      "type": "list",
      "items": [
        "Norway — the classic fjord country, with the long, deep Sognefjord among the most famous.",
        "Chile — a labyrinth of fjords and channels along its southern Patagonian coast.",
        "New Zealand — the dramatic Fiordland region on the South Island.",
        "Greenland, Alaska, and Canada — vast, often remote fjord coastlines carved by ice-age glaciers."
      ]
    },
    {
      "type": "paragraph",
      "text": "It is no coincidence that several of these places also rank among the countries with the longest coastlines. All those branching, winding inlets add up to enormous amounts of shoreline."
    },
    {
      "type": "heading",
      "text": "Spotting Fjords From Above"
    },
    {
      "type": "paragraph",
      "text": "From satellite view, fjords are a gift to anyone trying to identify a location. The signature is unmistakable: dark, finger-like channels of water reaching deep inland between rugged terrain, often with snow on the surrounding peaks. The pattern is so distinctive that recognizing it can immediately point you to a short list of high-latitude coastlines."
    },
    {
      "type": "heading",
      "text": "Fjords vs. Other Coastal Inlets"
    },
    {
      "type": "paragraph",
      "text": "Not every deep inlet is a fjord. A ria, for example, looks superficially similar but forms differently: it is a river valley, not a glacial one, that was drowned by rising seas, so it lacks the steep U-shaped walls and great depths of a true fjord. The glacial origin is what sets fjords apart, which is why you only find them where ice once flowed all the way to the sea. Recognizing that difference is part of reading a coastline correctly rather than just admiring it."
    },
    {
      "type": "heading",
      "text": "Why Fjords Matter to People"
    },
    {
      "type": "paragraph",
      "text": "Fjords are more than scenery. Their deep, sheltered waters make superb natural harbours, which is one reason coastal communities have thrived along them for centuries. Today that same calm, deep water supports industries like fish farming, while the steep terrain and heavy rainfall that surround many fjords are ideal for hydroelectric power. A fjord coastline is a landscape that has quietly shaped how entire regions live and work."
    }
  ]
};
